import React, { useEffect, useState } from "react";
import { toast } from "sonner";
import { Dialog, DialogContent, DialogHeader, DialogTitle } from "components/ui/dialog";
import { Popover, PopoverContent, PopoverTrigger } from "components/ui/popover";
import { Calendar } from "components/ui/calendar";
import { CalendarIcon, FileText, Send } from "lucide-react";
import { format } from "date-fns";
import { cn, formatUTCToIST } from "lib/utils";
import { Button } from "components/ui/button";
import { Input } from "components/ui/input";
import { Textarea } from "components/ui/textarea";
import { Label } from "components/ui/label";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "components/ui/select";
import { X } from "lucide-react";
import { Card, CardContent } from "components/ui/card";
import { geocodeLocation, type GeocodeResponse } from "../api/shipments";
import { createIncident, getIncidentDocument, updateIncident, INCIDENT_TYPES } from "../api/incident";

interface IncidentModalProps {
    isOpen: boolean
    onClose: () => void
    cnNumber: string
    mode: "create" | "view"
    incidentData?: any
    onSuccess?: () => void
}

export const IncidentModal: React.FC<IncidentModalProps> = ({ isOpen, onClose, cnNumber, mode, incidentData, onSuccess }) => {
    const [incidentDate, setIncidentDate] = useState<Date | undefined>();
    const [isDatePickerOpen, setIsDatePickerOpen] = useState<boolean>(false);
    const [incidentType, setIncidentType] = useState<string>("");
    const [notes, setNotes] = useState<string>("");

    const [address, setAddress] = useState<string>("");
    const [lat, setLat] = useState<number | null>(null);
    const [lon, setLon] = useState<number | null>(null);

    const [documents, setDocuments] = useState<File[]>([]);

    const [notesList, setNotesList] = useState<any[]>([]);
    const [documentList, setDocumentList] = useState<any[]>([]);

    // Location search states
    const [locationQuery, setLocationQuery] = useState("");
    const [geocoding, setGeocoding] = useState<boolean>(false);
    const [geocodeResult, setGeocodeResult] = useState<GeocodeResponse | null>(null);

    useEffect(() => {
        if (!isOpen) return;

        if (mode === "view" && incidentData) {
            const incident = incidentData.incident;

            setIncidentType(incident.incident_type);
            setIncidentDate(new Date(incident.incident_date));
            setAddress(incident.address || "");
            setLat(incident.lat ?? null);
            setLon(incident.lon ?? null);

            setNotes("");
            setNotesList(incidentData.notes || []);
            setDocumentList(incidentData.documents || []);
        }

        if (mode === "create") {
            setIncidentDate(undefined);
            setIncidentType("");
            setNotes("");
            setAddress("");
            setLat(null);
            setLon(null);

            setDocuments([]);
            setNotesList([]);
            setDocumentList([]);
        }
    }, [isOpen, mode, incidentData]);

    const handleFindLocation = async () => {
        if (!locationQuery.trim()) return;

        setGeocoding(true);
        try {
            const result = await geocodeLocation(locationQuery.trim());
            setGeocodeResult(result);

            if (!result.success) {
                toast.error(result.message || "Failed to find location");
            }
        } catch {
            toast.error("Failed to find location");
        } finally {
            setGeocoding(false);
        }
    };

    const handleAddLocation = () => {
        if (!geocodeResult?.success) return;

        setLat(geocodeResult.lat!);
        setLon(geocodeResult.lon!);
        setAddress(geocodeResult.formatted_address!);

        // collapse search section
        setLocationQuery("");
        setGeocodeResult(null);
    };

    const handleFileChange = (e: React.ChangeEvent<HTMLInputElement>) => {
        if (!e.target.files) return;

        const MAX_FILE_SIZE = 5 * 1024 * 1024; // 5MB
        const newFiles = Array.from(e.target.files);
        const oversized = newFiles.filter((file) => file.size > MAX_FILE_SIZE);

        if (oversized.length > 0) {
            toast.error(`File(s) exceed 5MB limit: ${oversized.map((f) => f.name).join(", ")}`);
            e.target.value = "";
            return;
        }

        const formattedDocs = newFiles.map((file) => ({
            id: `temp-${Date.now()}-${file.name}`,
            file_name: file.name,
            created_at: new Date().toISOString(),
            file: file,
            is_new: true
        }));

        setDocumentList((prev) => [...prev, ...formattedDocs]);

        e.target.value = "";
    };

    const handleRemoveFile = (id: string) => {
        setDocumentList((prev) => prev.filter((doc) => doc.id !== id));
    };

    const fileToBase64 = (file: File): Promise<string> => {
        return new Promise((resolve, reject) => {
            const reader = new FileReader();
            reader.readAsDataURL(file);
            reader.onload = () => {
                const result = reader.result as string;
                // remove "data:xxx;base64," prefix
                const base64 = result.split(",")[1];
                resolve(base64);
            };
            reader.onerror = (error) => reject(error);
        });
    };

    const handleSave = async () => {
        if (!incidentDate) {
            toast.error("Please select incident date");
            return;
        }

        if (!incidentType) {
            toast.error("Please select incident type");
            return;
        }

        try {
            const newDocuments = documentList.filter((doc) => doc.is_new);
            const formattedDocuments = await Promise.all(
                newDocuments.map(async (doc) => ({
                    document_type: "GENERAL",
                    mime_type: doc.file.type,
                    file_name: doc.file.name,
                    file_base64: await fileToBase64(doc.file),
                }))
            );

            const payload = {
                cn_number: cnNumber,
                incident_date: format(incidentDate, "yyyy-MM-dd") + "T00:00:00",
                incident_type: incidentType,
                lat: lat ?? undefined,
                lon: lon ?? undefined,
                address: address || undefined,
                notes: notes || undefined,
                documents: formattedDocuments,
            };

            await createIncident(payload);
            toast.success("Incident created successfully");
            onSuccess?.();
            onClose();
        } catch (error: any) {
            toast.error(error?.response?.data?.detail ?? error?.message ?? "Error creating incident");
        }
    };

    const handleUpdate = async () => {
        try {
            const newNotes = notesList.filter((n) => n.is_new);
            const newDocuments = documentList.filter((d) => d.is_new);

            const formattedDocuments = await Promise.all(
                newDocuments.map(async (doc) => ({
                    document_type: "GENERAL",
                    mime_type: doc.file.type,
                    file_name: doc.file.name,
                    file_base64: await fileToBase64(doc.file),
                }))
            );

            const payload = {
                incident_id: incidentData.incident.id,
                notes: newNotes.map((n) => n.notes),
                documents: formattedDocuments
            };

            await updateIncident(payload);
            toast.success("Incident updated successfully");
            onClose();
        } catch (error: any) {
            toast.error("Error updating incident");
        }
    };

    const handleAddNote = () => {
        if (!notes.trim()) return;

        const userData = localStorage.getItem("user_data");
        const fullName = userData ? JSON.parse(userData).full_name : "You";

        const newNote = {
            name: fullName,
            notes: notes,
            created_at: new Date().toISOString(),
            is_new: true
        };

        setNotesList((prev) => [...prev, newNote]);
        setNotes("");
    };

    const handleViewDocument = async (doc: any) => {
        try {
            const blob = await getIncidentDocument(doc.id);
            const url = window.URL.createObjectURL(blob);
            window.open(url, "_blank");
        } catch (error) {
            toast.error("Failed to open document");
        }
    };

    const isCreateValid = incidentDate && incidentType && address && lat !== null && lon !== null;

    return (
        <Dialog open={isOpen} onOpenChange={onClose}>
            <DialogContent className="max-w-lg max-h-[90vh] overflow-y-auto">
                <DialogHeader>
                    <DialogTitle>Incident Report - {cnNumber}</DialogTitle>
                </DialogHeader>

                <div className="space-y-4">
                    <div className="space-y-1">
                        <Label>Date of Incident</Label>
                        <div className="flex items-center border rounded-md bg-background">
                            <Popover open={isDatePickerOpen} onOpenChange={setIsDatePickerOpen}>
                                <PopoverTrigger asChild>
                                    <Button
                                        variant="ghost"
                                        disabled={mode === "view"}
                                        onClick={() => setIsDatePickerOpen(true)}
                                        className={cn(
                                            "h-9 px-3 py-2 justify-start text-left font-normal w-full rounded-md hover:bg-muted/50 text-sm",
                                            !incidentDate && "text-gray-500"
                                        )}
                                    >
                                        <CalendarIcon className="mr-2 h-4 w-4 flex-shrink-0" />

                                        <div className="flex items-center min-w-0">
                                            {incidentDate ? (
                                                <span className="text-sm truncate">
                                                    {format(incidentDate, "dd MMM yyyy")}
                                                </span>
                                            ) : (
                                                <span className="text-sm text-gray-500">
                                                    Select Date
                                                </span>
                                            )}
                                        </div>
                                    </Button>
                                </PopoverTrigger>

                                <PopoverContent className="w-auto p-0" align="start">
                                    <Calendar
                                        mode="single"
                                        selected={incidentDate}
                                        onSelect={(date) => {
                                            if (mode === "view") return;
                                            setIncidentDate(date);
                                            setIsDatePickerOpen(false);
                                        }}
                                        disabled={(date) => date > new Date()}
                                        initialFocus
                                    />
                                </PopoverContent>
                            </Popover>
                        </div>
                    </div>

                    <div>
                        <Label>Incident Type</Label>
                        <Select value={incidentType} onValueChange={setIncidentType} disabled={mode === "view"}>
                            <SelectTrigger>
                                <SelectValue placeholder="Select type" />
                            </SelectTrigger>
                            <SelectContent>
                                {INCIDENT_TYPES.map((type) => (
                                    <SelectItem key={type} value={type}>{type}</SelectItem>
                                ))}
                            </SelectContent>
                        </Select>
                    </div>

                    <div className="space-y-3">
                        <Label>Location</Label>
                        {address ? (
                            <div className="p-3 border rounded-md bg-gray-50 text-xs space-y-2">
                                <div>
                                    <strong>Address:</strong>
                                    <div>{address}</div>
                                </div>

                                {lat !== null && lon !== null && (
                                    <div>
                                        <strong>Coordinates:</strong>
                                        <div>
                                            {lat.toFixed(4)}, {lon.toFixed(4)}
                                        </div>
                                    </div>
                                )}

                                <div className="flex justify-end">
                                    <Button
                                        size="sm"
                                        variant="outline"
                                        disabled={mode === "view"}
                                        onClick={() => {
                                            setAddress("");
                                            setLat(null);
                                            setLon(null);
                                            setGeocodeResult(null);
                                            setLocationQuery("");
                                        }}
                                    >
                                        Change Location
                                    </Button>
                                </div>
                            </div>
                        ) : (
                            <>
                                <div className="flex gap-2">
                                    <Input
                                        placeholder="Enter location (e.g. Chakan, Pune)"
                                        value={locationQuery}
                                        onChange={(e) => setLocationQuery(e.target.value)}
                                    />
                                    <Button
                                        onClick={handleFindLocation}
                                        disabled={!locationQuery.trim() || geocoding}
                                    >
                                        {geocoding ? "Finding..." : "Find"}
                                    </Button>
                                </div>

                                {geocodeResult?.success && (
                                    <Card>
                                        <CardContent className="pt-4 text-xs space-y-2">
                                            <div className="font-medium text-green-700">
                                                Location Found
                                            </div>

                                            <div className="grid grid-cols-2 gap-2">
                                                <div>
                                                    <strong>Coordinates:</strong>
                                                    <div>
                                                        {(geocodeResult.lat ?? 0).toFixed(4)},{" "}
                                                        {(geocodeResult.lon ?? 0).toFixed(4)}
                                                    </div>
                                                </div>

                                                <div>
                                                    <strong>City:</strong>
                                                    <div>{geocodeResult.city}</div>
                                                </div>

                                                <div className="col-span-2">
                                                    <strong>Address:</strong>
                                                    <div>{geocodeResult.formatted_address}</div>
                                                </div>

                                                <div>
                                                    <strong>State:</strong>
                                                    <div>{geocodeResult.state}</div>
                                                </div>

                                                <div>
                                                    <strong>Country:</strong>
                                                    <div>{geocodeResult.country_code}</div>
                                                </div>
                                            </div>

                                            <div className="flex justify-end pt-2">
                                                <Button
                                                    size="sm"
                                                    className="bg-green-600 hover:bg-green-700"
                                                    onClick={handleAddLocation}
                                                >
                                                    Use This Location
                                                </Button>
                                            </div>
                                        </CardContent>
                                    </Card>
                                )}
                            </>
                        )}
                    </div>

                    <div className="space-y-2">
                        <Label>Notes / Comments</Label>
                        {mode === "view" ? (
                            <>
                                {notesList.length > 0 && (
                                    <div className="border rounded-md divide-y overflow-y-auto max-h-44">
                                        {notesList.map((note: any) => (
                                            <div key={`${note.name}-${note.created_at}`} className="px-3 py-2.5">
                                                <div className="flex items-center justify-between mb-1">
                                                    <span className="text-sm font-medium">{note.name}</span>
                                                    <span className="text-[11px] text-muted-foreground">{formatUTCToIST(note.created_at)}</span>
                                                </div>
                                                <div className="text-xs text-gray-700 whitespace-pre-wrap leading-relaxed">{note.notes}</div>
                                            </div>
                                        ))}
                                    </div>
                                )}

                                {notesList.length === 0 && (
                                    <p className="text-xs text-muted-foreground">No notes yet.</p>
                                )}

                                <div className="relative">
                                    <Textarea
                                        rows={2}
                                        placeholder="Add a note..."
                                        value={notes}
                                        onChange={(e) => setNotes(e.target.value)}
                                        className="pr-10"
                                    />
                                    <button
                                        type="button"
                                        onClick={handleAddNote}
                                        disabled={!notes.trim()}
                                        className="absolute bottom-2 right-2 flex items-center justify-center w-6 h-6 rounded-full bg-primary hover:bg-primary/90 text-primary-foreground transition disabled:opacity-40 disabled:cursor-not-allowed"
                                    >
                                        <Send className="w-3 h-3" />
                                    </button>
                                </div>
                            </>
                        ) : (
                            <Textarea
                                rows={3}
                                value={notes}
                                onChange={(e) => setNotes(e.target.value)}
                            />
                        )}
                    </div>

                    <div className="space-y-2">
                        <div className="flex items-baseline gap-2">
                            <Label>Documents</Label>
                            <span className="text-[11px] text-muted-foreground">Max 5 MB per file</span>
                        </div>
                        {mode === "view" && (
                            <div className="border rounded-lg max-h-56 overflow-y-auto p-3 bg-muted/50 space-y-2">
                                {incidentData?.documents?.length ? (
                                    documentList.map((doc: any) => (
                                        <div key={doc.id} className="group flex items-center justify-between p-3 bg-white border rounded-md hover:shadow-sm transition">
                                            {/* Left Section */}
                                            <div className="flex items-center gap-3 min-w-0">
                                                <div className="flex items-center justify-center w-8 h-8 rounded-md bg-blue-50 text-blue-600">
                                                    <FileText className="w-4 h-4" />
                                                </div>

                                                {/* File Info */}
                                                <div className="flex flex-col min-w-0">
                                                    <span className="text-sm font-medium truncate max-w-[220px]">
                                                        {doc.file_name}
                                                    </span>

                                                    <span className="text-[11px] text-muted-foreground">
                                                        {formatUTCToIST(doc.created_at)}
                                                    </span>
                                                </div>
                                            </div>

                                            {/* Right Section */}
                                            <div className="flex items-center gap-2">
                                                {doc.file ? (
                                                    <span className="text-[11px] text-emerald-600 font-medium">Uploaded</span>
                                                ) : (
                                                    <Button
                                                        size="sm"
                                                        variant="ghost"
                                                        onClick={() => handleViewDocument(doc)}
                                                        className="text-xs h-7 px-2"
                                                    >
                                                        View
                                                    </Button>
                                                )}
                                            </div>
                                        </div>
                                    ))
                                ) : (
                                    <div className="text-xs text-muted-foreground text-center py-6">No documents uploaded</div>
                                )}
                            </div>
                        )}

                        {/* Upload Input */}
                        <Input className="cursor-pointer" type="file" multiple onChange={handleFileChange} />

                        {/* New Uploaded Files */}
                        {documentList.filter((doc) => doc.is_new).length > 0 && (
                            <div className="flex flex-wrap gap-2 pt-2">
                                {documentList
                                    .filter((doc) => doc.is_new)
                                    .map((doc) => (
                                        <div key={doc.id} className="flex items-center gap-2 max-w-[220px] text-xs px-2.5 py-1.5 bg-muted rounded-md border">
                                            <FileText className="w-3.5 h-3.5 text-muted-foreground" />

                                            <span className="truncate">{doc.file_name}</span>

                                            <button
                                                type="button"
                                                onClick={() => handleRemoveFile(doc.id)}
                                                className="text-red-500 hover:text-red-600 transition"
                                            >
                                                <X className="h-3.5 w-3.5" />
                                            </button>
                                        </div>
                                    ))}
                            </div>
                        )}
                    </div>

                    <div className="flex justify-end space-x-2">
                        <Button variant="outline" onClick={onClose}>
                            Cancel
                        </Button>
                        {mode === "create" ? (
                            <Button 
                                onClick={handleSave}
                                disabled={!isCreateValid}
                                className={!isCreateValid ? "opacity-50 cursor-not-allowed" : ""}
                            >
                                Save
                            </Button>
                        ) : (
                            <Button onClick={handleUpdate}>Update</Button>
                        )}
                    </div>
                </div>
            </DialogContent>
        </Dialog>
    );
};